import { useState } from 'react';
import { Cloud, CheckCircle, XCircle, AlertCircle, RefreshCw, Database } from 'lucide-react';
import { googleCloudStorageService } from '../utils/googleCloudStorageService';

const GoogleCloudStorageTest = () => {
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isTestingUpload, setIsTestingUpload] = useState(false);
  const [connectionResult, setConnectionResult] = useState<any>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);

  const configInfo = googleCloudStorageService.getConfigInfo();

  const testConnection = async () => {
    setIsTestingConnection(true);
    setConnectionResult(null);
    
    try {
      const result = await googleCloudStorageService.testConnection();
      setConnectionResult(result);
    } catch (error) {
      setConnectionResult({
        success: false,
        message: 'Test failed with error',
        details: error
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const testUpload = async () => {
    setIsTestingUpload(true);
    setUploadResult(null);
    
    try {
      // Create a tiny 1x1 PNG image for upload testing
      const pngDataUrl = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAuMB9o2S2xUAAAAASUVORK5CYII=';
      const blob = await fetch(pngDataUrl).then(r => r.blob());
      const file = new File([blob], 'test-upload.png', { type: 'image/png' });

      const result = await googleCloudStorageService.uploadImage(file);
      setUploadResult({
        success: result.success,
        message: result.success ? 'Upload successful' : 'Upload failed',
        details: result.success ? { url: result.url } : result.error
      });
    } catch (error) {
      setUploadResult({
        success: false,
        message: 'Upload test failed with error',
        details: error
      });
    } finally {
      setIsTestingUpload(false);
    }
  };

  const StatusIcon = ({ success }: { success?: boolean }) => {
    if (success === undefined) return <AlertCircle className="h-5 w-5 text-gray-400" />;
    return success ? 
      <CheckCircle className="h-5 w-5 text-green-600" /> : 
      <XCircle className="h-5 w-5 text-red-600" />;
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Cloud className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            Google Cloud Storage Connection Test
          </h2>
        </div>

        {/* Configuration Info */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Current Configuration</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Provider:</span>
              <span className="text-sm text-gray-900 flex items-center">
                <Database className="h-4 w-4 mr-1 text-blue-600" />
                {configInfo.provider}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Project ID:</span>
              <div className="flex items-center space-x-2">
                <StatusIcon success={!!configInfo.projectId} />
                <span className="text-sm text-gray-900">
                  {configInfo.projectId || 'Not configured'}
                </span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Bucket Name:</span>
              <span className="text-sm text-gray-900">{configInfo.bucketName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Backend Base URL:</span>
              <span className="text-sm text-gray-900 break-all">{configInfo.apiBase || '(same-origin)'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Overall Status:</span>
              <div className="flex items-center space-x-2">
                <StatusIcon success={configInfo.isConfigured} />
                <span className="text-sm text-gray-900">
                  {configInfo.isConfigured ? 'Ready' : 'Needs configuration'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Test */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-gray-900">Bucket Connection</h3>
            <button
              onClick={testConnection}
              disabled={isTestingConnection || !configInfo.isConfigured}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTestingConnection ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Cloud className="h-4 w-4" />
              )}
              <span>
                {isTestingConnection ? 'Testing...' : 'Test Connection'}
              </span>
            </button>
          </div>

          {connectionResult && (
            <div className={`p-4 rounded-lg border ${
              connectionResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start space-x-3">
                <StatusIcon success={connectionResult.success} />
                <div className="flex-1">
                  <p className={`font-medium ${
                    connectionResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {connectionResult.message}
                  </p>
                  
                  {connectionResult.details && connectionResult.success && (
                    <div className="mt-2 text-sm text-green-700">
                      <p><strong>Bucket:</strong> {connectionResult.details.bucketName}</p>
                      <p><strong>Location:</strong> {connectionResult.details.location}</p>
                      <p><strong>Storage Class:</strong> {connectionResult.details.storageClass}</p>
                      <p><strong>Created:</strong> {new Date(connectionResult.details.timeCreated).toLocaleDateString()}</p>
                    </div>
                  )}
                  
                  {connectionResult.details && !connectionResult.success && (
                    <pre className="mt-2 text-xs text-red-700 bg-red-100 p-2 rounded overflow-x-auto">
                      {typeof connectionResult.details === 'string' 
                        ? connectionResult.details 
                        : JSON.stringify(connectionResult.details, null, 2)
                      }
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Upload Test */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-gray-900">Upload Functionality</h3>
            <button
              onClick={testUpload}
              disabled={isTestingUpload || !connectionResult?.success}
              className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTestingUpload ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Cloud className="h-4 w-4" />
              )}
              <span>
                {isTestingUpload ? 'Testing Upload...' : 'Test Upload'}
              </span>
            </button>
          </div>

          {uploadResult && (
            <div className={`p-4 rounded-lg border ${
              uploadResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start space-x-3">
                <StatusIcon success={uploadResult.success} />
                <div className="flex-1">
                  <p className={`font-medium ${
                    uploadResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {uploadResult.message}
                  </p>
                  
                  {uploadResult.details?.url && (
                    <div className="mt-2">
                      <p className="text-sm text-green-700">
                        <strong>Test file URL:</strong>
                      </p>
                      <a 
                        href={uploadResult.details.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 break-all"
                      >
                        {uploadResult.details.url}
                      </a>
                    </div>
                  )}

                  {uploadResult.details && typeof uploadResult.details === 'string' && (
                    <pre className="mt-2 text-xs text-red-700 bg-red-100 p-2 rounded overflow-x-auto">
                      {uploadResult.details}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Setup Instructions */}
        {!configInfo.isConfigured && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800 mb-2">Google Cloud Storage Setup Required</h4>
                <div className="text-sm text-blue-700 space-y-3">
                  <p>To configure Google Cloud Storage integration:</p>
                  
                  <div className="bg-blue-100 p-3 rounded text-xs">
                    <p className="font-medium mb-2">1. Create a <code>.env.local</code> file with:</p>
                    <pre className="whitespace-pre-wrap">
{`# Google Cloud Storage Configuration
VITE_GCS_PROJECT_ID=your-project-id
VITE_GCS_BUCKET_NAME=${configInfo.bucketName}`}
                    </pre>
                  </div>

                  <div className="space-y-2">
                    <p className="font-medium">2. Google Cloud Setup Steps:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-3 text-xs">
                      <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a></li>
                      <li>Create or select a project</li>
                      <li>Enable the Cloud Storage API</li>
                      <li>Create a storage bucket for your car images</li>
                      <li>Go to IAM & Admin → Service Accounts</li>
                      <li>Grant your Cloud Run backend service account Storage Admin access to the bucket</li>
                    </ol>
                  </div>

                  <div className="bg-yellow-100 p-2 rounded text-xs">
                    <p className="font-medium text-yellow-800">💡 For development:</p>
                    <p className="text-yellow-700">Run the backend locally (port 3002) and the frontend will POST to it automatically.</p>
                  </div>

                  <div className="bg-green-100 p-2 rounded text-xs">
                    <p className="font-medium text-green-800">🔒 For production:</p>
                    <p className="text-green-700">The frontend uses your Cloud Run backend for uploads—no client tokens required.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleCloudStorageTest;